<template>
  <div class="page-container billing-transfer-main-page">
    <portal to="page-name">{{ pageName }}</portal>
    <div class="card card-page">
      <div class="card-header">
        <button class="btn btn-gray btn-icon btn-icon-left float-right"
                v-if="canPrintReport"
                @click="reportModal.show = true">
          <fa-icon :icon="['fal', 'print']"/>Relatório
        </button>
        <h1 class="card-title">{{ title }}</h1>
      </div>
      <div class="card-body">
        <div class="columns">
          <div class="column col-5 col-md-8 col-sm-12 form-group"
               :class="{'has-error': $v.filter.startDate.$error
                || $v.filter.endDate.$error}">
            <label class="form-label">Período</label>
            <div class="input-group">
              <dx-dropdown
                class="input-group-addon"
                :items="periodOptions"
                label="text"
                @select="setPeriod"
                direction="left"
              ><fa-icon :icon="['fal', 'chevron-down']" /></dx-dropdown>
              <select class="form-select" style="max-width: 130px"
                      v-model="filter.dateType">
                <option value="account">Data conta</option>
                <option value="expense">Data do serviço</option>
              </select>
              <dx-input-date
                id="start-date"
                class="form-input text-center"
                v-model="filter.startDate"
                @change="changedStartDate"
                @blur="$v.filter.startDate.$touch()"
              />
              <dx-input-date
                id="end-date"
                class="form-input text-center"
                v-model="filter.endDate"
                @change="changedEndDate"
                @blur="$v.filter.endDate.$touch()"
              />
            </div>
          </div>
          <div class="column col-3 col-md-4 col-sm-12 form-group">
            <label class="form-label">Status da fatura</label>
            <st-multi-select
              v-model="filter.statuses"
              :items="statusItems"
              value-key="value"
              label-key="label"
            />
          </div>
        </div>
        <div class="columns">
          <div class="column col-5 col-md-12 col-sm-12 form-group">
            <label class="form-label">Convênio</label>
            <select class="form-select"
                    v-model="filter.insurancePlanId">
              <option value="">[Todos os convênios]</option>
              <option v-for="(insurance, i) in insurancePlans"
                      :value="insurance.id" :key="i">{{ insurance.name }}</option>
            </select>
          </div>
          <div class="column form-group">
            <label class="form-label">Profissional</label>
            <select class="form-select"
                    v-model="filter.professionalId">
              <option value="">[Todos os profissionais]</option>
              <option v-for="(professional, i) in professionals"
                      :value="professional.id" :key="i">{{ professional.name }}</option>
            </select>
          </div>
          <div class="column col-auto form-group"
               style="align-self: flex-end">
            <button class="btn btn-primary btn-icon-left mb-2"
                    :class="{loading}"
                    :disabled="loading"
                    @click="load">
              <fa-icon class="icon" :icon="['fal', 'search']"></fa-icon>
              Atualizar
            </button>
          </div>
        </div>
        <div class="loading loading-lg mt-2" v-if="loading"></div>
        <template v-else>
          <div class="divider" />
          <div class="h6">Profissionais</div>
          <template v-if="data.items.length === 0">
            <div class="empty mt-2">
              <div class="empty-icon">
                <fa-icon :icon="['fal', 'info-circle']" size="3x"/>
              </div>
              <p class="empty-title h5">Repasse</p>
              <p class="empty-subtitle">
                Nenhuma conta encontrada. Verifique os filtros para realizar novas buscas
              </p>
            </div>
          </template>
          <template v-else>
            <label class="form-switch">
              <input type="checkbox" v-model="filter.allValues">
              <i class="form-icon"></i>Exibir repasses zerados
            </label>
            <div v-for="(professional, i) in data.items" :key="i">
              <professional-transfer-item
                :api-path="apiPath"
                :legacy-only="legacyOnly"
                :data="professional"
                :filter="filter"
                @update="load"
              />
            </div>
          </template>
        </template>
      </div>
      <div class="card-footer" v-if="data.items.length > 0">
        <span>Quantidade de profissionais:
          <strong class="ml-2 text-primary">{{ data.total }}</strong>
        </span>
        <span class="float-right mr-1">Total:
          <strong class="text-primary">{{ total | currency }}</strong>
        </span>
      </div>
    </div>
    <dx-modal title="Relatório de repasses (legado)" size="lg"
              v-model="reportModal.show" id="modal-legacy-transfer-report"
              v-if="canPrintReport">
      <billing-professional-transfer-legacy
        v-if="reportModal.show"
        :print-path="`${apiPath}/print`"
      />
      <template slot="footer">
        <button class="btn" @click="reportModal.show = false">Fechar</button>
      </template>
    </dx-modal>
  </div>
</template>

<script>
import moment from 'moment';
import { REPORT_PROFESSIONAL_TRANSFER } from '@/data/actions/modules/financial';
import { required } from 'vuelidate/lib/validators';
// eslint-disable-next-line import/no-cycle
import * as professionalApi from '@/app/professional/api';
import { statuses as invoiceStatuses } from 'src/data/invoice-statuses';
import BillingProfessionalTransferLegacy from '@/app/financial-report/components/billingProfessionalTransferLegacy.vue';
import ProfessionalTransferItem from './TransferItem.vue';
import { date, minDate } from '../../../../../data/validators';

export default {
  props: {
    legacyOnly: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    BillingProfessionalTransferLegacy,
    ProfessionalTransferItem,
  },
  data() {
    return {
      reportModal: {
        show: false,
      },
      debounceTimeout: null,
      insurancePlans: [],
      professionals: [],
      loading: false,
      periodOptions: [],
      filter: {
        startDate: moment()
          .subtract(1, 'month')
          .startOf('month')
          .format('YYYY-MM-DD'),
        endDate: moment()
          .subtract(1, 'month')
          .endOf('month')
          .format('YYYY-MM-DD'),
        insurancePlanId: '',
        professionalId: '',
        dateType: 'account',
        statuses: ['finished'],
        allValues: true,
      },
      data: {
        items: [],
        total: 3,
        limit: 100,
        offset: 0,
      },
    };
  },
  mounted() {
    this.loadInsurances();
    this.loadProfessionals();
    this.loadPeriod();
    this.load();
  },
  computed: {
    pageName() {
      return this.legacyOnly ? 'Repasses (legado)' : 'Repasses';
    },
    title() {
      return this.legacyOnly ? 'Repasses (legado)' : 'Repasse dos profissionais';
    },
    canPrintReport() {
      return this.legacyOnly && this.$can(REPORT_PROFESSIONAL_TRANSFER);
    },
    apiPath() {
      return this.legacyOnly ? '/legacy/professional-transfer' : '/professional-transfer';
    },
    statusItems() {
      return Object.keys(invoiceStatuses).map(key => ({
        value: key,
        label: invoiceStatuses[key],
      }));
    },
    total() {
      return this.data.items.reduce((result, item) => {
        result += item.accounts.reduce((a, b) => a + Number(b.total), 0);
        return result;
      }, 0);
    },
  },
  validations() {
    const rules = {
      filter: {
        startDate: { required, date },
        endDate: { required, date },
      },
    };

    if (this.filter.startDate && moment(this.filter.startDate).isValid()) {
      rules.filter.endDate = {
        required,
        date,
        minDate: minDate(this.filter.startDate),
      };
    }

    return rules;
  },
  methods: {
    load() {
      this.$v.filter.$touch();
      if (this.$v.filter.$error) {
        return null;
      }

      const duration = moment.duration(moment(this.filter.endDate)
        .diff(moment(this.filter.startDate))).asMonths();

      if (duration > 12) {
        this.$toast.show('O intervalo entre as datas não pode ser superior a 12 meses!',
          { type: 'error' });
        return false;
      }

      this.loading = true;

      const params = {
        dateType: this.filter.dateType,
        startDate: this.filter.startDate,
        endDate: this.filter.endDate,
        statuses: this.filter.statuses.join(','),
        limit: 0,
      };

      if (this.filter.insurancePlanId) {
        params.insurancePlanId = this.filter.insurancePlanId;
      }

      if (this.filter.professionalId) {
        params.professionalId = this.filter.professionalId;
      }

      return this.$httpX.get(this.apiPath, { params })
        .then(({ data }) => {
          data.items.forEach(({ accounts }) => {
            accounts.forEach((item) => {
              item.selected = true;
              return item;
            });
          });
          this.data = data;
        })
        .catch()
        .then(() => {
          this.loading = false;
        });
    },
    loadInsurances() {
      const params = {
        limit: 0,
        active: true,
      };
      return this.$http.get('/insurance-plans', { params })
        .then(({ data }) => {
          this.insurancePlans = data.items.map(item => ({
            id: item.plan.id,
            type: item.type,
            name: item.customName,
          }));
        })
        .catch(() => {});
    },
    loadProfessionals() {
      return professionalApi.allActive()
        .then((data) => {
          this.professionals = data.items;
        })
        .catch(this.$toast.error);
    },
    loadPeriod() {
      const now = new Date();
      for (let i = 0; i < 6; i += 1) {
        this.periodOptions.push(
          {
            value: i,
            text: moment(now).subtract(i, 'month').format('MM/YYYY'),
          },
        );
      }
    },
    changedStartDate() {
      if (!this.filter.startDate) {
        this.filter.startDate = moment()
          .subtract(1, 'month')
          .startOf('month')
          .format('YYYY-MM-DD');
        this.filter.endDate = moment()
          .subtract(1, 'month')
          .endOf('month')
          .format('YYYY-MM-DD');
      }
    },
    changedEndDate() {
      if (!this.filter.startDate && !this.filter.endDate) {
        this.filter.startDate = moment()
          .subtract(1, 'month')
          .startOf('month')
          .format('YYYY-MM-DD');
        this.filter.endDate = moment()
          .subtract(1, 'month')
          .endOf('month')
          .format('YYYY-MM-DD');
      }
      if (!this.filter.endDate) {
        this.filter.endDate = moment(this.filter.startDate)
          .endOf('month')
          .format('YYYY-MM-DD');
      }
    },
    setPeriod({ value }) {
      this.filter.startDate = moment()
        .subtract(value, 'month')
        .startOf('month')
        .format('YYYY-MM-DD');
      this.filter.endDate = moment()
        .subtract(value, 'month')
        .endOf('month')
        .format('YYYY-MM-DD');
    },
  },
};
</script>

<style lang="scss">
  @import '../../../../../assets/scss/variables';
  @import 'src/assets/scss/colors';

  .billing-transfer-main-page {
  }
</style>
